import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, FormControlLabel, Link, Stack, Typography } from '@mui/material';
import OpenInNew from '@mui/icons-material/OpenInNew';
import SignupShell from '../../../components/SignupShell';
import HeaderText from '../../../common/HeaderText';
import { legalApi } from '../../../legal/api';
import Cookies from 'js-cookie';

export default function TermsOfService() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [attempt, setAttempt] = useState(0);
  const submitting = useRef(false);
  const agentData = state?.agentData;
  const actorId = agentData?.agent?.id;
  const sessionToken = agentData?.token;

  useEffect(() => {
    if (!actorId || !sessionToken) return;
    let cancelled = false;
    setLoading(true);
    setPolicies(null);
    setAgreed(false);
    setError('');
    legalApi.policies().then(value => { if (!cancelled) setPolicies(value); })
      .catch(() => { if (!cancelled) setError('Unable to load the current policies. Please try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [actorId, sessionToken, attempt]);

  if (!actorId || !sessionToken) return <Navigate to="/register" replace />;

  const continueSignup = async event => {
    event.preventDefault();
    if (!agreed || !policies || loading || submitting.current) return;
    if (Cookies.get('token') !== sessionToken) {
      setError('Your account changed. Please restart signup.');
      return;
    }
    submitting.current = true;
    setSaving(true);
    setError('');
    try {
      const receipt = await legalApi.accept(policies, sessionToken);
      if (receipt.actor_id !== Number(actorId) || Cookies.get('token') !== sessionToken) {
        throw new Error('Your account changed. Please restart signup.');
      }
      navigate('/create-workspace', {
        replace: true,
        state: { termsOfService: true, legalAcceptance: receipt, agentData },
      });
    } catch (failure) {
      if (failure.code === 'legal_policy_version_mismatch') {
        setAgreed(false);
        setNotice('The policies have changed. Please review them and agree again.');
        setAttempt(value => value + 1);
      } else {
        setError(failure.message || 'Unable to save your agreement. Please try again.');
      }
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return <SignupShell>
    <Box component="form" onSubmit={continueSignup}>
      <Stack spacing={2}>
        <HeaderText variant="h3" fontSize={32} textContent="Terms of Service" />
        <Typography>Please read and agree to the Terms of Service and Privacy Policy to continue.</Typography>
        {notice && <Alert severity="info">{notice}</Alert>}
        {error && <Alert severity="error" action={!policies && !loading ?
          <Button onClick={() => setAttempt(value => value + 1)}>Retry</Button> : undefined}>{error}</Alert>}
        {loading && <Typography role="status">Loading policies…</Typography>}
        {policies && <Box component="nav" aria-label="Legal information" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Link href={policies.terms.url} target="_blank" rel="noopener noreferrer"
            aria-label="Terms of Service (opens in a new tab)"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            Terms of Service <OpenInNew aria-hidden="true" sx={{ fontSize: 18 }} />
          </Link>
          <Link href={policies.privacy.url} target="_blank" rel="noopener noreferrer"
            aria-label="Privacy Policy (opens in a new tab)"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            Privacy Policy <OpenInNew aria-hidden="true" sx={{ fontSize: 18 }} />
          </Link>
        </Box>}
        <FormControlLabel
          control={<Checkbox checked={agreed} disabled={!policies || loading || saving}
            onChange={event => setAgreed(event.target.checked)} />}
          label="I have read and agree to the Terms of Service and Privacy Policy"
          sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 1, lineHeight: 1.6 } }}
        />
        <Button type="submit" variant="contained" fullWidth disabled={!agreed || !policies || loading || saving} sx={{ py: 1 }}>
          {saving ? 'Saving agreement…' : 'Continue'}
        </Button>
      </Stack>
    </Box>
  </SignupShell>;
}
